import React, { useRef, Children } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.1, 0.25, 1]
    }
  }
};
interface HighlightItemProps {
  text: string;
  index: number;
}
function HighlightItem({
  text,
  index
}: HighlightItemProps) {
  return <motion.li className="flex items-center gap-4" variants={{
    hidden: {
      opacity: 0,
      x: -12
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.1,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  }} data-id="element-77">
      <span className="w-4 h-px bg-accent flex-shrink-0" aria-hidden="true" data-id="element-78" />
      <span className="font-body text-base text-dark/70 tracking-wide" data-id="element-79">
        {text}
      </span>
    </motion.li>;
}
export function HeritageSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(contentRef, {
    once: true,
    margin: '-80px'
  });
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const contentY = useTransform(scrollYProgress, [0, 1], [20, -20]);
  return <section ref={sectionRef} className="relative w-full bg-light px-6 py-20 md:py-26 overflow-hidden" aria-label="Location and context" data-id="element-80">
      <motion.div ref={contentRef} className="mx-auto max-w-3xl" style={{
      y: contentY
    }} data-id="element-81">
        <motion.div className="mb-12" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={fadeUpVariants} data-id="element-82">
          <p className="font-heading text-xs font-bold tracking-widest-plus uppercase text-accent mb-4" data-id="element-83">
            Location
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-dark" data-id="element-84">
            Connected &amp; Cultured
          </h2>
        </motion.div>

        <motion.div className="space-y-6 mb-14" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.15,
            delayChildren: 0.1
          }
        }
      }} data-id="element-85">
          <motion.p className="font-body text-base md:text-lg leading-relaxed text-dark/75" variants={fadeUpVariants} data-id="element-86">
            North Melbourne represents the perfect synthesis of industrial
            heritage and contemporary lifestyle. Just moments from the CBD, this
            precinct has evolved into one of Melbourne's most sought-after
            destinations, characterized by wide tree-lined streets, converted
            warehouses, and a vibrant culinary scene.
          </motion.p>

          <motion.p className="font-body text-base md:text-lg leading-relaxed text-dark/75" variants={fadeUpVariants} data-id="element-87">
            120-124 Dryburgh Street is strategically positioned to capitalize on
            this connectivity. A short walk connects you to the iconic Queen
            Victoria Market, Errol Street's village atmosphere, and the future
            Arden Station precinct. With immediate access to CityLink and major
            arterials, it offers unrivaled logistical convenience in a premium
            city-fringe setting.
          </motion.p>
        </motion.div>

        <motion.ul className="space-y-4" role="list" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.3
          }
        }
      }} data-id="element-88">
          <HighlightItem text="Melbourne CBD — 1km*" index={0} data-id="element-89" />
          <HighlightItem text="Errol Street Village — 600m*" index={1} data-id="element-90" />
          <HighlightItem text="Queen Victoria Market — 900m*" index={2} data-id="element-91" />
          <HighlightItem text="North Melbourne Station — 800m*" index={3} data-id="element-92" />
        </motion.ul>
      </motion.div>
    </section>;
}